import React from 'react';
import { Link } from 'react-router-dom';
import RestaurantExempleBooking from './RestaurantExempleBooking';

export function useRestaurantContext(rawProps, { site, slug, interactive, commandeUrl }) {
  const cartTo = commandeUrl || (slug ? `/lp/${encodeURIComponent(slug)}/commande` : '#commande');
  const reservation = site?.reservation || {};
  const year = new Date().getFullYear();
  const seasonalTags = String(rawProps.seasonalTags || '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
  return { cartTo, reservation, year, seasonalTags, slug, interactive };
}

export function RestaurantChromeBlock({ p, ctx }) {
  const { cartTo, slug, interactive, navOpen, setNavOpen, closeNav } = ctx;
  return (
    <>
      <div className="announcement">
        <span className="leaf">✳</span> {p.announcement}
        <span className="announcement-dot">·</span>
      </div>
      <header className="site-header">
        <a className="brand" href="#accueil" aria-label={`${p.brandLine1} ${p.brandLine2}, accueil`}>
          <span className="brand-mark" aria-hidden="true">
            ✳
          </span>
          <span className="brand-name">
            {p.brandLine1} <em>{p.brandLine2}</em>
          </span>
        </a>
        <button
          type="button"
          className="menu-toggle"
          aria-label={navOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={navOpen}
          onClick={() => setNavOpen((v) => !v)}
        >
          <span />
          <span />
        </button>
        <nav className={`nav ${navOpen ? 'open' : ''}`}>
          <a href="#histoire" onClick={closeNav}>
            Notre esprit
          </a>
          {interactive && slug ? (
            <Link to={cartTo} onClick={closeNav}>
              {p.menuCartLabel || 'La carte'}
            </Link>
          ) : (
            <a href={cartTo} onClick={closeNav}>
              {p.menuCartLabel || 'La carte'}
            </a>
          )}
          <a href="#galerie" onClick={closeNav}>
            Le lieu
          </a>
          <a href="#infos" onClick={closeNav}>
            Infos pratiques
          </a>
          <a className="nav-cta" href="#reservation" onClick={closeNav}>
            Réserver une table <span>↗</span>
          </a>
        </nav>
      </header>
    </>
  );
}

export function RestaurantHeroBlock({ p }) {
  return (
    <section className="hero" id="accueil">
      <div
        className="hero-image"
        role="img"
        aria-label="Table élégante dans un jardin luxuriant"
        style={{ backgroundImage: `url('${p.heroImage}')` }}
      />
      <div className="hero-shade" />
      <div className="hero-content">
        <p className="eyebrow light">
          <span /> {p.heroEyebrow}
        </p>
        <h1 dangerouslySetInnerHTML={{ __html: p.heroTitleHtml }} />
        <p className="hero-copy" dangerouslySetInnerHTML={{ __html: p.heroCopy }} />
        <div className="hero-actions">
          <a className="button button-cream" href="#reservation">
            Réserver ma table <span>↗</span>
          </a>
          <a className="text-link light-link" href="#carte">
            {p.heroDiscoverLabel || 'Découvrir la carte'} <span>↓</span>
          </a>
        </div>
      </div>
      <div className="hero-side-note">
        <span className="vertical-line" />
        PRODUITS DE SAISON · CUISINE INSPIRÉE
      </div>
      <div className="hero-bottom">
        <span>UNE PARENTHÈSE GOURMANDE</span>
        <span className="scroll-cue">
          DÉFILER <b>↓</b>
        </span>
      </div>
    </section>
  );
}

export function RestaurantIntroBlock({ p }) {
  return (
    <section className="intro section-pad" id="histoire">
      <div className="intro-stamp">
        <span>DE LA TERRE</span>
        <b>✳</b>
        <span>À L&apos;ASSIETTE</span>
      </div>
      <div className="intro-copy">
        <p className="eyebrow">
          <span /> {p.introEyebrow}
        </p>
        <h2 dangerouslySetInnerHTML={{ __html: p.introTitleHtml }} />
        <p className="body-copy">{p.introBody}</p>
        <a className="underlined-link" href="#carte">
          Notre philosophie <span>↗</span>
        </a>
      </div>
      <div className="intro-photo-wrap">
        <img src={p.introImage} alt="Assiette gastronomique dressée avec soin" />
        <div className="photo-caption">
          LE PLAISIR DU FAIT MAISON <span>—</span> CHAQUE JOUR
        </div>
      </div>
    </section>
  );
}

export function RestaurantSeasonalBlock({ p, ctx }) {
  const { seasonalTags } = ctx;
  return (
    <section className="seasonal" id="carte">
      <div
        className="seasonal-image"
        style={{ backgroundImage: `url('${p.seasonalImage}')` }}
      />
      <div className="seasonal-content">
        <p className="eyebrow light">
          <span /> {p.seasonalEyebrow}
        </p>
        <h2 dangerouslySetInnerHTML={{ __html: p.seasonalTitleHtml }} />
        <p>{p.seasonalText}</p>
        <div className="seasonal-tags">
          {seasonalTags.map((tag) => (
            <span key={tag}>{tag}</span>
          ))}
        </div>
        <a className="button button-outline" href="#reservation">
          Venez goûter <span>↗</span>
        </a>
      </div>
      <div className="seasonal-number">
        01 <span>/</span> 03
      </div>
    </section>
  );
}

export function RestaurantDishesBlock({ p }) {
  return (
    <section className="menu-section section-pad">
      <div className="section-heading">
        <div>
          <p className="eyebrow">
            <span /> {p.menuEyebrow}
          </p>
          <h2 dangerouslySetInnerHTML={{ __html: p.menuTitleHtml }} />
        </div>
        <p className="heading-note" dangerouslySetInnerHTML={{ __html: p.menuNote }} />
      </div>
      <div className="menu-grid">
        {p.dishCards.map((dish, idx) => (
          <article
            key={`${dish.title}-${idx}`}
            className={`dish-card ${dish.offset ? 'dish-offset' : ''}`}
          >
            <div
              className="dish-img"
              style={dish.image ? { backgroundImage: `url('${dish.image}')` } : undefined}
            />
            <div className="dish-meta">
              <span>{dish.metaLeft}</span>
              <span>{dish.metaRight}</span>
            </div>
            <h3>{dish.title}</h3>
            <p>{dish.description}</p>
          </article>
        ))}
      </div>
      <p className="menu-disclaimer">{p.menuDisclaimer}</p>
    </section>
  );
}

export function RestaurantQuoteBlock({ p }) {
  return (
    <section className="quote-section">
      <span className="quote-mark">“</span>
      <blockquote dangerouslySetInnerHTML={{ __html: p.quoteHtml }} />
      <p>{p.quoteBy}</p>
      <div className="quote-flower">✳</div>
    </section>
  );
}

export function RestaurantGalleryBlock({ p }) {
  return (
    <section className="gallery section-pad" id="galerie">
      <div className="section-heading">
        <div>
          <p className="eyebrow">
            <span /> {p.galleryEyebrow}
          </p>
          <h2 dangerouslySetInnerHTML={{ __html: p.galleryTitleHtml }} />
        </div>
        <p className="heading-note" dangerouslySetInnerHTML={{ __html: p.galleryNote }} />
      </div>
      <div className="gallery-grid">
        {p.galleryImages.map((src, i) => (
          <img
            key={`${src}-${i}`}
            src={src}
            alt={p.galleryImageAlts?.[i] || ''}
            loading="lazy"
          />
        ))}
      </div>
    </section>
  );
}

export function RestaurantReservationBlock({ p, ctx }) {
  const { site, slug, interactive, reservation } = ctx;
  return (
    <section className="reservation-section" id="reservation">
      <div className="reservation-backdrop" />
      <div className="reservation-inner">
        <div className="reservation-copy">
          <p className="eyebrow light">
            <span /> {p.reservationEyebrow}
          </p>
          <h2 dangerouslySetInnerHTML={{ __html: p.reservationTitleHtml }} />
          <p>{p.reservationLead}</p>
          <div className="reservation-note">
            <span>✳</span>
            <div>
              <b>{p.reservationNoteTitle}</b>
              <small>{p.reservationNoteText}</small>
            </div>
          </div>
        </div>
        <RestaurantExempleBooking
          slug={slug}
          site={site}
          reservation={reservation}
          bookingKicker={p.bookingKicker}
          bookingTitle={p.bookingTitle}
          interactive={interactive}
        />
      </div>
    </section>
  );
}

export function RestaurantPracticalBlock({ p }) {
  return (
    <section className="practical section-pad" id="infos">
      <div>
        <p className="eyebrow">
          <span /> {p.practicalEyebrow}
        </p>
        <h2 dangerouslySetInnerHTML={{ __html: p.practicalTitleHtml }} />
      </div>
      <div className="practical-grid">
        <div>
          <span className="info-icon">⌖</span>
          <h3>Nous trouver</h3>
          <p dangerouslySetInnerHTML={{ __html: p.addressHtml }} />
          <a
            className="underlined-link"
            href="https://maps.google.com"
            target="_blank"
            rel="noreferrer"
          >
            Ouvrir l&apos;itinéraire ↗
          </a>
        </div>
        <div>
          <span className="info-icon">◷</span>
          <h3>Nos horaires</h3>
          <p dangerouslySetInnerHTML={{ __html: p.hoursHtml }} />
          <small>Horaires à confirmer</small>
        </div>
        <div>
          <span className="info-icon">✆</span>
          <h3>Nous contacter</h3>
          <p dangerouslySetInnerHTML={{ __html: p.contactHtml }} />
          <a className="underlined-link" href={`mailto:${p.contactEmail}`}>
            Écrivez-nous ↗
          </a>
        </div>
      </div>
    </section>
  );
}

export function RestaurantFooterBlock({ p, ctx }) {
  const { year } = ctx;
  return (
    <>
      <footer className="footer">
        <a className="brand footer-brand" href="#accueil">
          <span className="brand-mark">✳</span>
          <span className="brand-name">
            {p.brandLine1} <em>{p.brandLine2}</em>
          </span>
        </a>
        <span>{p.footerTagline}</span>
        <div>
          <a href="#infos">Contact</a>
          <a href="#accueil">Mentions légales</a>
          <a href="#accueil">Confidentialité</a>
        </div>
        <small>
          © {year} {p.brandLine1} {p.brandLine2}
        </small>
      </footer>
      <div className="mobile-booking">
        <a href="#reservation">
          Réserver une table <span>↗</span>
        </a>
      </div>
    </>
  );
}

const MAIN_VARIANTS = [
  'hero',
  'intro',
  'seasonal',
  'dishes',
  'quote',
  'gallery',
  'reservation',
  'practical',
];

export function RestaurantBlock({ variant, p, ctx }) {
  switch (variant) {
    case 'chrome':
      return <RestaurantChromeBlock p={p} ctx={ctx} />;
    case 'hero':
      return <RestaurantHeroBlock p={p} />;
    case 'intro':
      return <RestaurantIntroBlock p={p} />;
    case 'seasonal':
      return <RestaurantSeasonalBlock p={p} ctx={ctx} />;
    case 'dishes':
      return <RestaurantDishesBlock p={p} />;
    case 'quote':
      return <RestaurantQuoteBlock p={p} />;
    case 'gallery':
      return <RestaurantGalleryBlock p={p} />;
    case 'reservation':
      return <RestaurantReservationBlock p={p} ctx={ctx} />;
    case 'practical':
      return <RestaurantPracticalBlock p={p} />;
    case 'footer':
      return <RestaurantFooterBlock p={p} ctx={ctx} />;
    default:
      return null;
  }
}

export { MAIN_VARIANTS };
